import { DEFAULT_ARENA_ID, arenas, tilesetFor } from '@archon3d/content';
import {
  AFFLICTION_ROUNDS,
  createGame,
  duelLevelSkill,
  arenaForGame,
  type ArenaDef,
  type TilesetDef,
  type Roster,
  type GameContext,
  type GameState,
  type ArenaAiSkill,
  type PendingBattle,
  type Piece,
  type FighterId,
} from '@archon3d/core';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import type { Engine } from '@babylonjs/core/Engines/engine';
import { HDRFiltering } from '@babylonjs/core/Materials/Textures/Filtering/hdrFiltering';
import { MirrorTexture } from '@babylonjs/core/Materials/Textures/mirrorTexture';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Plane } from '@babylonjs/core/Maths/math.plane';
import { Vector3, type Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import { WebXRControllerComponent } from '@babylonjs/core/XR/motionController/webXRControllerComponent';
import type { WebXRDefaultExperience } from '@babylonjs/core/XR/webXRDefaultExperience';
import { duelReference, type AudioBus } from '../audio/audioBus';
import { attachSoundDirector, type MusicSource } from '../audio/soundDirector';
import { createXr, enterVr, isImmersiveVrSupported, watchXrState } from '../engine/xr';
import { reportError } from '../telemetry';
import { createXrTableInput } from '../input/xrTableInput';
import type { BattleFeed } from '../game/battleFeed';
import type { ArenaBattle, GameSession } from '../game/session';
import { DEFAULT_SETTINGS, type LookSettings } from '../ui/settings';
import type { ShellStore } from '../ui/shell';
import type { ArenaFrame } from './arena/arenaFrame';
import { createArenaStage, type ArenaStageEvent } from './arena/arenaStage';
import { ArenaSkies, themeSkyLevel } from './arena/arenaSky';
import { attachBoardView, type BoardView, type Cell } from './board/boardView';
import {
  createBoardPadController,
  type BoardPadController,
  type PadZone,
} from '../input/boardPadController';
import { CharacterModelCache } from './board/characterModels';
import { BOARD_CENTER, BOARD_FIT_RADIUS, BOARD_TOP_Y, COLORS } from './board/constants';
import { createEnvironment, createGradientTexture } from './board/environment';
import { createBoardFog } from './board/fog';
import { createHighlights, type Highlights } from './board/highlights';
import { createLighting } from './board/lighting';
import { PALETTES, paletteFor, type Palette } from './board/palette';
import { createPieceLayer, type PieceLayer } from './board/pieces';
import { boardReach } from './board/frame';
import { cellToWorld } from './board/layout';
import { facingFaction, seatYawFor } from './board/orientation';
import { createBoardTiles, type BoardTiles } from './board/tiles';
import { applyShadowQuality, capShadowQuality } from './shadowQuality';
import { createGameTablets, LARGE_TEXT, type GameTablets } from './tablets/gameTablets';
import { createCardStage } from './card/cardStage';
import { createImmersiveControls } from './immersiveControls';
import { createConnectionPaneXr } from './connectionPaneXr';
import { connectionPane as connectionPaneFor } from '../ui/connectionPane';
import {
  createFloatingPages,
  showsFloatingPage,
  showOnlyPage,
  EMPTY_PAGE_SPACE,
  canMoveBoard,
} from './floatingPages';
import { WebXRState } from '@babylonjs/core/XR/webXRTypes';

/**
 * Which characters a duel staged for a test should put in the arena, by character id. Anything
 * left out, or that the side does not field, falls back to one of its pieces at random. It exists
 * so a test can look at a particular match-up — a ghost's ring cannot be looked at if it turns up
 * one duel in eight.
 */
export interface PracticePick {
  attacker?: string;
  defender?: string;
  /** Lays Curse on one side first, so a test can look at a cursed fighter (D-31). */
  cursed?: FighterId;
}

export interface GameSceneHandle {
  scene: Scene;
  xrSupported: boolean;
  models: CharacterModelCache;
  /** The wooden tablets on the table that carry the game's information and controls. */
  readonly tablets: GameTablets;
  /** Puts another game on the table: the board, pieces and tablets follow, the room stays. */
  setSession(session: GameSession): void;
  enterVr(): Promise<void>;
  exitVr(): Promise<void>;
  resetView(): void;
  /**
   * A duel staged outside any game, leaving the board untouched: the browser tests' way into the
   * arena. Players have none since the owner removed practice fights (2026-09-26).
   */
  practiceFight(pick?: PracticePick): void;
  /** A person gives up the duel that is running: the first person's side unless one is named. */
  forfeit(side?: FighterId): void;
  /** A duel's outcome has been read: back to the board now. */
  continueOutcome(): void;
  /** Where on the canvas the arena is framed, as shares of it from the top left; for tests. */
  arenaFrame(): ArenaFrame;
  /** The running duel's light, for tests: night or day, and the sun's strength. */
  arenaDaylight(): { night: boolean; sunIntensity: number };
  /** Moves a staged duel's clock on, for tests of its late minutes. */
  skipDuelClock(seconds: number): void;
  /** How well the AI fights in the arena (0…1 accuracy and speed); takes effect from the next duel. */
  setArenaSkill(skill: ArenaAiSkill): void;
  arenaSkill(): ArenaAiSkill;
  /** Heavy smoke drifting over the board, a game setting; off until asked for. */
  setFog(enabled: boolean): void;
  /** How thick the smoke is, 0…1. */
  setFogDensity(density: number): void;
  /**
   * Graphics, VR comfort, the palette and the tablets' lettering, applied at once: shadows and
   * reflections within what the headset allows, the comfort options into the arena and the
   * seat, the colours onto everything on the table.
   */
  applyLook(look: LookSettings): void;
  /** The look applied last, for tests. */
  look(): LookSettings;
  /** Odds on attack targets, warnings under threatened pieces and the tablet's line about them. */
  setThreatHints(on: boolean): void;
  /** What the threat hints show right now, for tests. */
  hints(): { labels: string[]; threatened: number[]; signs: string[] };
  /** The cell under the board cursor (mouse or gamepad), for tests. */
  hover(): Cell | null;
  /** Where a cell is drawn on the screen, for tests that move the mouse to it. */
  screenPointOf(cell: Cell): { x: number; y: number } | null;
  /** Where the gamepad's attention is, for tests. */
  padZone(): PadZone;
  dispose(): void;
}

const BASE_EXPOSURE = 1.1;
const NIGHT_CLEAR = new Color4(0.03, 0.04, 0.08, 1);
/** Daylight over the arena: blue zenith, hazy horizon, green haze below the ground line. */
const DAY_SKY: [number, string][] = [
  [0, '#3f7fd6'],
  [0.45, '#a9cdf2'],
  [0.5, '#d7e8f7'],
  [0.56, '#8fae7a'],
  [1, '#41603a'],
];
const DIVE_SECONDS = 0.45;
/** How quickly the board turns to a new seat (fraction of the remaining angle per second). */
const SEAT_TURN_RATE = 3;

interface Fade {
  from: number;
  to: number;
  t: number;
  seconds: number;
  resolve: () => void;
}

interface Dive {
  from: { alpha: number; beta: number; radius: number; target: Vector3 };
  to: { radius: number; target: Vector3 };
  t: number;
}

/** Everything on the table that belongs to one game and goes with it. */
interface Mounted {
  session: GameSession;
  tiles: BoardTiles;
  highlights: Highlights;
  pieces: PieceLayer;
  tablets: GameTablets;
  view: BoardView;
  /** A game controller on the board screen, beside the mouse. */
  pad: BoardPadController;
  /** Piece meshes registered for shadows and reflections, to unregister on the way out. */
  casters: AbstractMesh[];
  unmount(): void;
}

/**
 * One scene for the whole game: the board as a table-scale diorama (planar reflections on desktop,
 * a static sky reflection in the headset) and, switched in for every capture, the tilemap arena.
 * Sharing the scene keeps the XR session alive across the two, and across games: a new game
 * swaps the board and pieces on the table without rebuilding the room or leaving VR.
 */
export async function createGameScene(
  engine: Engine,
  session: GameSession,
  feed: BattleFeed,
  shell: ShellStore,
  audio: AudioBus,
  /** The radio, which the ambience gives way to and whose Music slider it follows. */
  music?: MusicSource,
): Promise<GameSceneHandle> {
  // One roster serves every board for now; the arena preloads its fighters from it.
  const { roster } = session.getSnapshot().ctx;
  const canvas = engine.getRenderingCanvas();
  const scene = new Scene(engine);
  scene.clearColor = NIGHT_CLEAR;
  scene.skipPointerMovePicking = true;
  scene.imageProcessingConfiguration.toneMappingEnabled = true;
  scene.imageProcessingConfiguration.exposure = BASE_EXPOSURE;

  // Framed so the board and the tablets on the near edge of the table are both in view.
  const camera = new ArcRotateCamera(
    'orbit',
    Math.PI / 2,
    1.08,
    1.72,
    BOARD_CENTER.add(new Vector3(0, 0.06, 0.1)),
    scene,
  );
  camera.lowerRadiusLimit = 0.45;
  camera.upperRadiusLimit = 4;
  // Pass the horizon so a browser player can tilt up beneath the temple roof and inspect it.
  camera.upperBetaLimit = Math.PI - 0.15;
  camera.lowerBetaLimit = 0.15;
  camera.wheelDeltaPercentage = 0.02;
  camera.panningSensibility = 0;
  camera.minZ = 0.02;
  camera.maxZ = 200;
  camera.attachControl(canvas, true);

  const boardRoot = new TransformNode('board', scene);
  // The board turns on the table so the player's own pieces are nearest: a player sits behind
  // the pieces they play. The table, the tablets and the VR floor stay put.
  const turntable = new TransformNode('turntable', scene);
  turntable.parent = boardRoot;
  turntable.setPivotPoint(BOARD_CENTER);
  // The room the table stands in: its floor, the table itself, the temple and the land round it.
  // It stays in the headset while a duel stands on the table (D-16); a screen's duel takes its
  // place at true size.
  const roomRoot = new TransformNode('room', scene);
  const environment = createEnvironment(scene, roomRoot);
  const room = roomRoot.getChildMeshes(false);
  // The pieces' shadows are the board's sun's alone; the temple has a sun of its own.
  const lighting = createLighting(scene, {
    lit: environment.templeLit,
    casters: [environment.temple],
  });
  // The smoke belongs to the table, not to one game: it stays on across sessions.
  const fog = createBoardFog(scene, turntable);
  const daySky = createGradientTexture(scene, 'daySky', DAY_SKY);
  const themeSkies = new ArenaSkies(scene);

  const mirror = new MirrorTexture('boardMirror', 512, scene, true);
  mirror.mirrorPlane = Plane.FromPositionAndNormal(
    new Vector3(0, BOARD_TOP_Y, 0),
    new Vector3(0, -1, 0),
  );
  mirror.renderList = [environment.sky];
  mirror.level = 0.45;

  const models = new CharacterModelCache(scene);

  // The sky probe doubles as image-based lighting for the PBR character models once it has been
  // prefiltered for roughness; until then (and if filtering fails) the raw cube map is used.
  scene.environmentTexture = environment.probe.cubeTexture;
  // Filtered once the sky is in the probe. Filtered after the first frame, as it was, a cold load
  // had not decoded the sky's picture yet, and a blank probe lit the models for the whole session
  // (client-board:quality#8, client-board:bugs#9). A sky that never loads leaves the raw cube.
  const waitForSky = scene.onAfterRenderObservable.add(() => {
    if (!environment.nightSky.isReady() || !environment.sky.isReady(true)) return;
    scene.onAfterRenderObservable.remove(waitForSky);
    const cube = environment.probe.cubeTexture;
    // Drawn again with the sky in; filtered at the end of that frame, all six faces done.
    cube.onAfterRenderObservable.addOnce(() =>
      scene.onAfterRenderObservable.addOnce(() => {
        new HDRFiltering(engine)
          .prefilter(cube)
          .catch((err: unknown) => console.warn('environment prefilter failed', err));
      }),
    );
    cube.resetRefreshCounter();
  });

  const powerColor = Color3.FromHexString(COLORS.powerPoint);
  let look: LookSettings = DEFAULT_SETTINGS;
  let palette: Palette = PALETTES.classic;
  const textScale = (of: LookSettings) => (of.tabletText === 'large' ? LARGE_TEXT : 1);
  let mounted: Mounted | null = null;
  let gameView = 0;
  let t = 0;
  let fade: Fade | null = null;
  let dive: Dive | null = null;
  let seatYaw = 0;
  scene.onBeforeRenderObservable.add(() => {
    const dt = engine.getDeltaTime() / 1000;
    t += dt;
    if (roomRoot.isEnabled()) {
      environment.update(t);
      lighting.update(t);
    }
    tableInput?.update(dt, canMoveBoard(shell.getSnapshot(), fighting));
    if (boardRoot.isEnabled()) fog.update(dt);
    if (mounted)
      powerColor.scaleToRef(
        0.7 + 0.3 * Math.sin(t * 2.4),
        mounted.tiles.markerMaterial.emissiveColor,
      );
    // Turn the board towards the seat that plays now, the short way round.
    let turn = seatYaw - turntable.rotation.y;
    while (turn > Math.PI) turn -= 2 * Math.PI;
    while (turn < -Math.PI) turn += 2 * Math.PI;
    if (Math.abs(turn) > 1e-4)
      turntable.rotation.y +=
        Math.abs(turn) < 0.002 ? turn : turn * Math.min(1, dt * SEAT_TURN_RATE);
    if (fade) {
      fade.t = Math.min(1, fade.t + dt / fade.seconds);
      const k = fade.from + (fade.to - fade.from) * fade.t;
      scene.imageProcessingConfiguration.exposure = BASE_EXPOSURE * k * k;
      if (fade.t >= 1) {
        fade.resolve();
        fade = null;
      }
    }
    if (dive) {
      dive.t = Math.min(1, dive.t + dt / DIVE_SECONDS);
      const k = dive.t * dive.t;
      camera.radius = dive.from.radius + (dive.to.radius - dive.from.radius) * k;
      Vector3.LerpToRef(dive.from.target, dive.to.target, k, camera.target);
      if (dive.t >= 1) dive = null;
    }
  });

  // Fades ride on the image-processing exposure, which materials apply themselves: no
  // post-processing, so it works in the headset too.
  const fadeTo = (target: number, seconds: number) =>
    new Promise<void>((resolve) => {
      fade?.resolve();
      fade = {
        from: Math.sqrt(scene.imageProcessingConfiguration.exposure / BASE_EXPOSURE),
        to: target,
        t: 0,
        seconds,
        resolve,
      };
    });

  let inXr = false;
  let fighting = false;
  /** A duel began on the screen, so the orbit camera is off: it comes back when the duel is over. */
  let fightOnScreen = false;
  /** A duel stands on the table in the headset, with the room round it (`showWorld`). */
  let duelOnTable = false;
  let orbitBeforeDive: Dive['from'] | null = null;
  // A setting, so it outlives the game on the table; on until the app says otherwise.
  let threatHints = true;

  let xr: WebXRDefaultExperience | undefined;
  let tableInput: ReturnType<typeof createXrTableInput> | undefined;
  let immersiveControls: ReturnType<typeof createImmersiveControls> | undefined;
  let floatingPages: ReturnType<typeof createFloatingPages> | undefined;
  let connectionPane: ReturnType<typeof createConnectionPaneXr> | undefined;
  let savedBoardRig: { position: Vector3; rotation: Quaternion } | null = null;
  /** Reset View was pressed while a page was open: it is done as the page closes. */
  let resetOnClose = false;
  // The headset camera hangs under a node whose height is the seated-play offset, so a player
  // on a chair sees the table from standing height; teleports and the board's turntable are
  // untouched, and the arena hangs the camera back here after a duel.
  const xrSeat = new TransformNode('xrSeat', scene);
  if (await isImmersiveVrSupported()) {
    // A browser that offers VR and then refuses the helper still gets its table on the screen,
    // with no VR button (client-audio-engine:bugs#2).
    xr = await createXr(scene, [environment.floor]).catch((error: unknown) => {
      reportError(error, 'VR setup');
      return undefined;
    });
  }
  if (xr) {
    xr.baseExperience.camera.parent = xrSeat;
    tableInput = createXrTableInput(
      xr,
      xrSeat,
      () => look.comfort.seatedHeight,
      () => ({ free: look.comfort.freeTurning, snapDegrees: look.comfort.snapTurnDegrees }),
    );
    immersiveControls = createImmersiveControls(scene, shell, () =>
      xr!.baseExperience.exitXRAsync(),
    );
    floatingPages = createFloatingPages(scene, xr.baseExperience.camera, shell, () =>
      xr!.baseExperience.exitXRAsync(),
    );
    connectionPane = createConnectionPaneXr(scene, xr.baseExperience.camera, () =>
      mounted?.session.reclaimSeat(),
    );
    xr.pointerSelection.raySelectionPredicate = (mesh) =>
      mesh.isPickable &&
      mesh.isEnabled() &&
      mesh.isVisible &&
      (mesh.layerMask & xr!.baseExperience.camera.layerMask) !== 0;
    watchXrState(xr, (active) => {
      inXr = active;
      shell.set({ inXr: active });
      // A duel begun on the screen and ended in the headset: off comes the headset, back comes the
      // board's camera. Babylon brought back the arena's, over the hidden arena, with no mouse
      // control until a reload (client-arena:bugs#5, client-board:bugs#10).
      if (!active && fightOnScreen && !fighting) backToOrbit();
      // Entering the headset counts as a gesture, and the context may need a nudge on the Quest.
      if (active) audio.unlock();
      refreshQuality();
    });
  }

  /** The screen's camera back over the board where a duel dived in from, and in the mouse's hands. */
  const backToOrbit = () => {
    fightOnScreen = false;
    dive = null;
    if (orbitBeforeDive) {
      camera.alpha = orbitBeforeDive.alpha;
      camera.beta = orbitBeforeDive.beta;
      camera.radius = orbitBeforeDive.radius;
      camera.target.copyFrom(orbitBeforeDive.target);
      orbitBeforeDive = null;
    }
    scene.activeCamera = camera;
    camera.attachControl(canvas, true);
  };

  const buildStage = (arenaDef: ArenaDef, tileset: TilesetDef, fighters: Roster) =>
    createArenaStage({
      scene,
      engine,
      models,
      roster: fighters,
      arena: arenaDef,
      tileset,
      feed,
      xr,
      fade: fadeTo,
      room,
      // A duel staged outside any game (the tests') meets Fair's opponent, as the practice fight
      // did by default before the owner removed it (2026-09-26); a game's seats bring their own.
      aiSkill: duelLevelSkill('fair'),
      onModeChange: (isFighting) => {
        fighting = isFighting;
        showWorld(showsFloatingPage(shell.getSnapshot()));
        refreshQuality();
        if (inXr) return;
        if (isFighting) {
          fightOnScreen = true;
          camera.detachControl();
        } else backToOrbit();
      },
    });

  /** The arena a fight is fought in: its game's, by the square, or the bundled default. */
  const arenaOf = (ctx: GameContext, state?: Pick<GameState, 'pendingBattle'>) =>
    ctx.definition
      ? arenaForGame(ctx.definition, state)
      : { arena: arenas[DEFAULT_ARENA_ID]!, tileset: tilesetFor(arenas[DEFAULT_ARENA_ID]!) };
  // Built in the first game's own default arena, where its first fight is most likely fought.
  const startup = arenaOf(session.getSnapshot().ctx);
  let arena = buildStage(startup.arena, startup.tileset, roster);
  // Fetched as the arena is built, so its sky is in by the time a duel dives into it.
  themeSkies.get(arena.theme?.sky);
  // Stable event bridge: sound listeners must survive switching to a cell's arena.
  const arenaListeners = new Set<(event: ArenaStageEvent) => void>();
  const forwardArenaEvent = (event: ArenaStageEvent) => {
    for (const listener of arenaListeners) listener(event);
  };
  let detachArenaEvents = arena.onEvent(forwardArenaEvent);
  // The stage built at startup is kept when a duel is fought in the same arena with the same
  // roster; it used to be thrown away and built again at the first capture, as the dive began.
  let arenaKey = JSON.stringify([startup, roster]);
  function selectArena(ctx: GameContext, state: Pick<GameState, 'pendingBattle'>) {
    const selected = arenaOf(ctx, state);
    const key = JSON.stringify([selected, ctx.roster]);
    if (key === arenaKey) return;
    const skill = arena.aiSkill;
    detachArenaEvents();
    arena.dispose();
    arena = buildStage(selected.arena, selected.tileset, ctx.roster);
    themeSkies.get(arena.theme?.sky);
    detachArenaEvents = arena.onEvent(forwardArenaEvent);
    arena.setAiSkill(skill);
    arenaKey = key;
    arena.setPalette(palette);
    refreshQuality();
  }

  /**
   * What of the world shows. Between duels the board and the room; in a duel on a screen the arena
   * alone, at true size under a day sky; in a duel in the headset the room as well, round the arena
   * standing on its table (D-16), lit by the board's lights, which leave the arena to its own. A
   * floating page shows alone.
   */
  function showWorld(pages: boolean) {
    const onTable = fighting && arena.onTable;
    if (onTable !== duelOnTable) {
      duelOnTable = onTable;
      lighting.sky.includedOnlyMeshes = onTable ? [...room] : [];
      lighting.sun.includedOnlyMeshes = onTable ? [...room] : [];
      refreshQuality();
    }
    const roomShown = !fighting || onTable;
    boardRoot.setEnabled(!fighting && !pages);
    roomRoot.setEnabled(roomShown && !pages);
    lighting.sky.setEnabled(roomShown);
    lighting.sun.setEnabled(roomShown);
    lighting.templeSun.setEnabled(roomShown);
    environment.sky.setEnabled(!pages);
    // On a screen the duel has the sky of its square's hour: day on a light one, night on a dark
    // (x-aaa-gap#2), and its theme's own panorama where it names one (audit step 26b). On the
    // table in the headset the room keeps its own.
    const openSky = fighting && !onTable;
    const light = arena.daylight;
    const themed = openSky ? themeSkies.get(arena.theme?.sky) : null;
    environment.skyMaterial.emissiveTexture =
      themed ?? (openSky && !light.night ? daySky : environment.nightSky);
    if (themed) themed.level = themeSkyLevel(light);
    else if (openSky) daySky.level = light.skyLevel;
    scene.clearColor = pages ? EMPTY_PAGE_SPACE : openSky ? light.clear : NIGHT_CLEAR;
  }

  function refreshQuality() {
    // Planar reflections need one extra scene render per frame – fine on a PC, not on a Quest 2,
    // and pointless while the board is hidden behind the arena.
    const mirrorOn = look.graphics.reflections && !inXr && !fighting;
    if (mounted)
      mounted.tiles.material.reflectionTexture = mirrorOn ? mirror : environment.probe.cubeTexture;
    const targets = scene.customRenderTargets;
    const index = targets.indexOf(mirror);
    if (!mirrorOn && index >= 0) targets.splice(index, 1);
    if (mirrorOn && index < 0) targets.push(mirror);
    // The setting caps what the headset does and never raises it: the board's shadows stay at
    // low there, and the arena's chunks get none at all, as before.
    const { shadows } = look.graphics;
    // Nothing on the board casts while a duel stands on the table in its place.
    applyShadowQuality(
      lighting.shadows,
      duelOnTable ? 'off' : inXr ? capShadowQuality(shadows, 'low') : shadows,
    );
    lighting.templeSun.shadowEnabled = shadows !== 'off';
    environment.setClouds(!inXr);
    arena.setShadows(inXr ? 'off' : shadows);
  }

  /** Everything on the table that carries a colour of the palette. */
  const paint = (parts: Mounted) => {
    parts.tiles.setPalette(palette);
    parts.highlights.setPalette(palette);
    parts.pieces.setPalette(palette);
    parts.tablets.setPalette(palette);
  };

  const applyLook = (next: LookSettings) => {
    // Settings changes (including audio) must not overwrite the travelled/pivoted XR rig. A new
    // seated height moves it by the difference, and the board rig a floating page has put aside
    // as well: the page's slider is where the height is set, and closing the page restores that.
    const rise = next.comfort.seatedHeight - look.comfort.seatedHeight;
    xrSeat.position.y += rise;
    if (savedBoardRig) savedBoardRig.position.y += rise;
    look = next;
    refreshQuality();
    const chosen = paletteFor(next.palette);
    if (chosen !== palette) {
      palette = chosen;
      arena.setPalette(palette);
      if (mounted) paint(mounted);
    }
    mounted?.tablets.setTextScale(textScale(next));
  };

  // Sounds are placed relative to whichever camera looks now: the orbit camera over the table
  // (pieces a metre away), the duel's fixed camera tens of metres back from the arena on a
  // screen, or the player's head a reach from the arena standing on the table in the headset.
  audio.setListener(() => {
    const eye = scene.activeCamera;
    if (!eye) return null;
    const position = eye.globalPosition;
    const forward = eye.getDirection(Vector3.Forward());
    const right = eye.getDirection(Vector3.Right());
    return {
      position: { x: position.x, y: position.y, z: position.z },
      forward: { x: forward.x, y: forward.y, z: forward.z },
      right: { x: right.x, y: right.y, z: right.z },
      reference: fighting ? duelReference(position, arena.centre()) : 1,
    };
  });

  const enterVrNow = () =>
    xr ? enterVr(xr) : Promise.reject(new Error('VR is not available in this browser'));

  scene.onBeforeRenderObservable.add(() => {
    const pages = showsFloatingPage(shell.getSnapshot());
    if (xr) {
      if (pages && !savedBoardRig) {
        savedBoardRig = {
          position: xrSeat.position.clone(),
          rotation: xrSeat.rotationQuaternion!.clone(),
        };
        // Remove artificial board rotation/translation while retaining physical head tracking.
        tableInput?.level();
      } else if (!pages && savedBoardRig) {
        // Reset View pressed on the menu's page: the head goes back to the seat as the page goes.
        if (resetOnClose) tableInput?.reset();
        else {
          xrSeat.position.copyFrom(savedBoardRig.position);
          xrSeat.rotationQuaternion!.copyFrom(savedBoardRig.rotation);
          xrSeat.computeWorldMatrix(true);
        }
        savedBoardRig = null;
        resetOnClose = false;
      }
      showOnlyPage(xr.baseExperience.camera, pages, 0x0fffffff);
      if (xr.baseExperience.state === WebXRState.IN_XR || !inXr) floatingPages?.update();
      // Anchor only after restoring the board rig and receiving the actual XR pose.
      // All Board/Card/Arena views share the far-side world-fixed control plate.
      const view = fighting ? 'arena' : mounted?.session.getSnapshot().cardClash ? 'card' : 'board';
      immersiveControls?.update(
        inXr && xr.baseExperience.state === WebXRState.IN_XR && !pages
          ? `${gameView}:${view}`
          : null,
        xr.input.controllers.some(
          (c) =>
            c.motionController?.getComponentOfType(WebXRControllerComponent.SQUEEZE_TYPE)?.pressed,
        ),
      );
      // A dropped connection's pane (D-29): on screen the page shows it, in the headset this does.
      const snapshot = mounted?.session.getSnapshot();
      connectionPane?.update(
        inXr && xr.baseExperience.state === WebXRState.IN_XR && !pages && snapshot?.online
          ? connectionPaneFor(snapshot.online, performance.now(), snapshot.state.phase === 'ended')
          : null,
      );
      // On floating pages sticks scroll content, never move/rotate the camera.
      if (pages) {
        for (const controller of xr.input.controllers) {
          const stick = controller.motionController?.getComponentOfType('thumbstick');
          if (stick && Math.abs(stick.axes.y) > 0.2) {
            floatingPages?.scroll(stick.axes.y * engine.getDeltaTime() * 0.6);
            break;
          }
        }
      }
    }
    showWorld(pages);
    if (mounted && boardRoot.isEnabled())
      mounted.tablets.update(engine.getDeltaTime() / 1000, inXr);
    // The pad works the board on a screen only: the headset has its own controllers, and a duel
    // has the arena's pad. A dive into a square is not a moment to step the cursor either.
    mounted?.pad.update(
      engine.getDeltaTime() / 1000,
      !inXr && !fighting && !pages && !dive && boardRoot.isEnabled(),
    );
  });

  /** Desktop: the orbit camera plunges towards the contested square while the picture fades. */
  const diveInto = (battle: PendingBattle) => {
    if (inXr || dive || !mounted) return;
    orbitBeforeDive = {
      alpha: camera.alpha,
      beta: camera.beta,
      radius: camera.radius,
      target: camera.target.clone(),
    };
    const board = mounted.session.getSnapshot().ctx.board;
    const square = Vector3.TransformCoordinates(
      cellToWorld(board, battle.x, battle.y),
      turntable.getWorldMatrix(),
    );
    dive = { from: orbitBeforeDive, to: { radius: 0.3, target: square }, t: 0 };
  };

  /**
   * The seat is behind the pieces of the faction a person plays: whose, `facingFaction` says; a
   * table of several people keeps its seat while an AI moves (`settled`). A hex board only turns in
   * sixths so its sides stay parallel with the table's (`seatYawFor`).
   */
  const seatFor = (game: GameSession, settled: boolean): number => {
    const { state, seats, ctx } = game.getSnapshot();
    const humans = ctx.board.def.factions.filter((f) => seats[f.id] === 'human').map((f) => f.id);
    const facing = facingFaction(state, humans, settled);
    if (facing === undefined) return seatYaw;
    const faction = ctx.board.def.factions.find((f) => f.id === facing);
    if (!faction) return seatYawFor(ctx.board, null);
    const home = ctx.board.def.placements.filter((p) => p.faction === faction.id);
    if (home.length === 0) return seatYawFor(ctx.board, null);
    const cx = home.reduce((sum, p) => sum + p.x, 0) / home.length;
    const cy = home.reduce((sum, p) => sum + p.y, 0) / home.length;
    const at = cellToWorld(ctx.board, cx, cy);
    // Bring the direction from the centre to this home edge round to +z, the player's side.
    return seatYawFor(ctx.board, -Math.atan2(at.x - BOARD_CENTER.x, at.z - BOARD_CENTER.z));
  };

  const practiceFight = (want: PracticePick = {}) => {
    if (arena.active || !mounted) return;
    const ctx = mounted.session.getSnapshot().ctx;
    const seed = (Math.random() * 0xffffffff) >>> 0;
    const state = createGame(ctx, { seed });
    /** A piece of this side, of the named character when one is asked for and the side has it. */
    const pick = (faction: string, character: string | undefined): Piece => {
      const mine = state.pieces.filter((p) => p.faction === faction);
      const named = character ? mine.filter((p) => p.character === character) : [];
      const candidates = named.length > 0 ? named : mine;
      return candidates[Math.floor(Math.random() * candidates.length)]!;
    };
    const [home, away] = ctx.board.def.factions;
    if (!home || !away) return;
    const attacker = pick(home.id, want.attacker);
    const defender = pick(away.id, want.defender);
    const cursedId =
      want.cursed === 'attacker' ? attacker.id : want.cursed === 'defender' ? defender.id : null;
    const game = mounted.session;
    selectArena(ctx, {
      pendingBattle: {
        attackerId: attacker.id,
        defenderId: defender.id,
        x: defender.x,
        y: defender.y,
      },
    });
    arena.start({
      game: ctx,
      state: {
        ...state,
        pieces: state.pieces.map((p) =>
          p.id === cursedId ? { ...p, cursed: AFFLICTION_ROUNDS } : p,
        ),
        phase: 'battle',
        activeFaction: attacker.faction,
        pendingBattle: {
          attackerId: attacker.id,
          defenderId: defender.id,
          x: defender.x,
          y: defender.y,
        },
      },
      humans: ['attacker'],
      seed,
      practice: true,
      // The test hook's own opponent, set by `setArenaSkill`, unassisted.
      humanFriendlyAi: false,
      onFinished: () => queueMicrotask(() => mounted?.session === game && watch(game)),
    });
  };

  /** Captures that involve a person are fought in the arena; the result goes back to the rules. */
  let started: ArenaBattle | null = null;
  const watch = (game: GameSession) => {
    const snapshot = game.getSnapshot();
    seatYaw = seatFor(game, true);
    const pending = snapshot.arena;
    if (!pending || pending === started || arena.active || !snapshot.state.pendingBattle) return;
    started = pending;
    selectArena(snapshot.ctx, snapshot.state);
    diveInto(snapshot.state.pendingBattle);
    arena.start({
      game: snapshot.ctx,
      state: snapshot.state,
      humans: pending.humans,
      devices: pending.devices,
      humanFriendlyAi: pending.humanFriendlyAi,
      seed: pending.seed,
      practice: false,
      lockstep: pending.lockstep,
      skills: pending.skills,
      levels: pending.levels,
      onFinished: (result) => {
        // The game may have been swapped out during the duel; only its own result counts.
        if (mounted?.session !== game) return;
        game.resolveBattle(result);
        queueMicrotask(() => mounted?.session === game && watch(game));
      },
    });
  };

  const mount = (game: GameSession): Mounted => {
    const { board } = game.getSnapshot().ctx;
    // A board bigger than the classic 9×9 would hang over the table's edge: the turntable shrinks
    // it (pieces and highlights with it) until its frame fits inside the table top.
    turntable.scaling.setAll(Math.min(1, BOARD_FIT_RADIUS / (boardReach(board) + 0.03)));
    const tiles = createBoardTiles(scene, board, turntable);
    const highlights = createHighlights(scene, board, turntable);
    mirror.renderList?.push(tiles.markers);
    const casters: AbstractMesh[] = [];
    const pieces = createPieceLayer(
      scene,
      board,
      game.getSnapshot().ctx.roster,
      models,
      (mesh) => {
        casters.push(mesh);
        lighting.shadows.addShadowCaster(mesh);
        mirror.renderList?.push(mesh);
      },
      turntable,
    );
    // The sounds follow this game's session and pieces, so they come and go with the mount.
    const sounds = attachSoundDirector({
      bus: audio,
      session: game,
      music,
      pieces,
      arena: {
        onEvent: (listener) => {
          arenaListeners.add(listener);
          return () => {
            arenaListeners.delete(listener);
          };
        },
      },
    });
    const tablets = createGameTablets({
      scene,
      parent: boardRoot,
      session: game,
      shell,
      resetView: () => tableInput?.reset(),
      onGemTap: (gem) => sounds.gemTap(gem),
    });
    const view = attachBoardView(scene, game, { tiles, pieces, highlights, tablets });
    const pad = createBoardPadController({ camera, view, tablets, session: game });
    const cardStage = createCardStage(scene, boardRoot, turntable, game);
    view.setThreatHints(threatHints);
    tablets.setThreatHints(threatHints);
    started = null;
    const unsubscribe = game.subscribe(() => watch(game));
    seatYaw = seatFor(game, false);
    turntable.rotation.y = seatYaw;
    const unmount = () => {
      unsubscribe();
      pad.dispose();
      view.detach();
      sounds.dispose();
      tablets.dispose();
      cardStage.dispose();
      pieces.dispose();
      for (const mesh of casters) lighting.shadows.removeShadowCaster(mesh);
      const gone = new Set<AbstractMesh>([...casters, tiles.markers]);
      mirror.renderList = (mirror.renderList ?? []).filter((m) => !gone.has(m));
      highlights.dispose();
      tiles.dispose();
    };
    const result = {
      session: game,
      tiles,
      highlights,
      pieces,
      tablets,
      view,
      pad,
      casters,
      unmount,
    };
    mounted = result;
    paint(result);
    tablets.setTextScale(textScale(look));
    refreshQuality();
    watch(game);
    return result;
  };

  mount(session);

  return {
    scene,
    xrSupported: xr !== undefined,
    models,
    get tablets() {
      return mounted!.tablets;
    },
    setSession: (next) => {
      if (mounted?.session === next) return;
      // A duel of the game leaving the table ends with it (client-board:quality#15), and the
      // screen's camera comes out of the square it was diving into.
      if (arena.active) {
        arena.abort();
        if (!inXr && (dive || orbitBeforeDive)) backToOrbit();
      }
      mounted?.unmount();
      mounted = null;
      gameView++;
      mount(next);
    },
    enterVr: enterVrNow,
    exitVr: () => xr?.baseExperience.exitXRAsync() ?? Promise.resolve(),
    resetView: () => {
      if (savedBoardRig) resetOnClose = true;
      else tableInput?.reset();
    },
    practiceFight,
    forfeit: (side) => arena.forfeit(side),
    continueOutcome: () => arena.continueOutcome(),
    arenaFrame: () => arena.frame(),
    arenaDaylight: () => ({
      night: arena.daylight.night,
      sunIntensity: arena.daylight.sunIntensity,
    }),
    skipDuelClock: (seconds) => arena.skipClockTo(seconds),
    setArenaSkill: (skill) => arena.setAiSkill(skill),
    arenaSkill: () => ({ ...arena.aiSkill }),
    setFog: (enabled) => fog.setEnabled(enabled),
    setFogDensity: (density) => fog.setDensity(density),
    applyLook,
    look: () => look,
    setThreatHints: (on) => {
      threatHints = on;
      mounted?.view.setThreatHints(on);
      mounted?.tablets.setThreatHints(on);
    },
    hints: () => mounted?.view.hints() ?? { labels: [], threatened: [], signs: [] },
    hover: () => mounted?.view.hover() ?? null,
    screenPointOf: (cell) => mounted?.view.screenPointOf(cell) ?? null,
    padZone: () => mounted?.pad.zone() ?? 'board',
    dispose: () => {
      mounted?.unmount();
      mounted = null;
      audio.setListener(null);
      fog.dispose();
      arena.dispose();
      xr?.dispose();
      immersiveControls?.dispose();
      floatingPages?.dispose();
      connectionPane?.dispose();
      models.dispose();
      scene.dispose();
    },
  };
}
